import { AuthConsumerRenderProps } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Table } from 'semantic-ui-react';
import { saveUnfit } from 'services/patientMedicalDocumentEditReportServices';
import { UnfitDto, UpsertUnfitDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import { composeValidators, mustBeNumber, mustBePositiveNumber, required } from 'utils/validatorUtils';

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  patientId: number,
  documentId: number,
  currentTreaterId: number,
  unfit: UnfitDto,
  onCloseEditUnfit: () => void
}

const percents: Array<number> = [0, 25, 50, 75, 100];
const percentsOptions: Array<DropdownOption> = percents.map((percent: number, index: number) => ({
  key: index,
  text: percent.toString(),
  value: percent
}));

interface State {
  percents: Array<DropdownOption>,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertUnableToWorkModal extends Component<Props, State> {

  private initialValues: Partial<UnfitDto> = {};

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();
    const {unfit} = this.props;

    if (unfit) {
      const isMember = (percent: DropdownOption) => {
        return percent.value.toString() === unfit.percent.toString();
      };

      if (!percentsOptions.find(isMember)) {
        percentsOptions.push({
          key: unfit.percent,
          text: unfit.percent.toString(),
          value: unfit.percent
        });
      }

      this.initialValues = {
        ...unfit
      }
    }

    this.state = {
      percents: percentsOptions,
      cancelTokenSource: cancelTokenSource
    }

  };

  handleSubmit = (values: Partial<UnfitDto>) => {
    const {patientId, documentId, currentTreaterId, onCloseEditUnfit} = this.props;
    const {cancelTokenSource} = this.state;

    const request: Partial<UpsertUnfitDto> = {
      documentId: documentId,
      fromDate: values.fromDate,
      toDate: values.toDate,
      percent: values.percent,
      treatmentId: values.treatmentId,
      archived: values.archived,
      treaterId: currentTreaterId,
      patientId: patientId
    }

    saveUnfit(request, cancelTokenSource)
        .then(onCloseEditUnfit)
        .catch((e: any) => this.handleError(e.response.data))

  }

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.invalidDocumentId
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(
              getMessage(violation.details !== "" ? violation.details : `error.${violation.errorCode}`)
          )
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  renderForm = ({handleSubmit, submitting, values}: FormRenderProps) => {

    const {onCloseEditUnfit, getMessage} = this.props;
    const {percents, errorMessage} = this.state;

    const disabledBefore = values.fromDate && new Date(values.fromDate);

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage(undefined)}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column>
                    <Table celled selectable>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>{getMessage('from')}</Table.HeaderCell>
                          <Table.HeaderCell>{getMessage('to')}</Table.HeaderCell>
                          <Table.HeaderCell>%</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell>
                            <Field
                                name="fromDate"
                                component={DatePicker}
                                validate={required}
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <Field
                                name="toDate"
                                component={DatePicker}
                                disabledBefore={disabledBefore}
                                validate={required}
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <Field
                                name="percent"
                                component={Select}
                                options={percents}
                                search
                                selection
                                allowAdditions
                                upward
                                onKeyDown={ensureOnlyNumericInput}
                                validate={composeValidators(mustBeNumber, mustBePositiveNumber, required)}
                            />
                          </Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table>
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>
            <FormButtonsRow>
              <Button
                  compact
                  primary
                  disabled={submitting}
              >
                {getMessage("button.save")}
              </Button>
              <Button
                  compact
                  type="button"
                  onClick={() => onCloseEditUnfit()}>
                {getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  }

  render(): React.ReactNode {

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={this.initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }
}

export default withMessages(UpsertUnableToWorkModal)
